import React, { useState, useMemo } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, LogOut, Keyboard, Search } from 'lucide-react';
import Drawer from '../common/Drawer';
import Avatar from '../common/Avatar';
import Icon from '../common/Icon';
import ShortcutsModal from '../common/ShortcutsModal';
import CommandPalette from '../common/CommandPalette';
import PoweredBy from '../common/PoweredBy';
import NotificationBell from '../notifications/NotificationBell';
import useKeyboard from '../../hooks/useKeyboard';

// Layout compartilhado pra painéis logados (admin/supplier/reseller/client).
// Sidebar em ≥md; hamburger + Drawer em mobile.
//
// Props:
//   user: { name, email, avatarUrl? }
//   nav: [{ label, to, icon, end? }] ou [{ title, items: [...] }] pra grupos
//   onLogout: () => void
//   topbarRight: ReactNode (bell/search custom)
//   children: page content
const DashboardLayout = ({ user, nav = [], onLogout, topbarRight, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    setMenuOpen(false);
    onLogout?.();
    navigate('/entrar');
  };

  // Achata nav pra virar fonte do CommandPalette.
  const flatNavItems = useMemo(() => {
    const groups = nav.length && nav[0].items ? nav : [{ items: nav }];
    return groups.flatMap((g) =>
      (g.items || []).map((it) => ({
        id: `nav-${it.to}`,
        label: it.label,
        to: it.to,
        icon: it.icon ? <Icon as={it.icon} size="sm" /> : null,
        hint: it.shortcut ? `g ${it.shortcut}` : 'Ir',
        keywords: 'nav menu',
        shortcut: it.shortcut,
      }))
    );
  }, [nav]);

  // Shortcuts ativos em qualquer lugar do painel.
  // `g <letra>` navega pro item de nav correspondente; registrado dinamicamente.
  const shortcuts = useMemo(() => {
    const map = {
      'mod+k': () => setPaletteOpen((v) => !v),
      '?': () => setShortcutsOpen((v) => !v),
      '/': () => {
        const input =
          document.querySelector('[data-shortcut-search]') ||
          document.querySelector('input[type="search"]');
        input?.focus();
      },
      Escape: () => {
        setPaletteOpen(false);
        setShortcutsOpen(false);
      },
    };
    flatNavItems.forEach((item) => {
      if (item.shortcut) map[`g ${item.shortcut}`] = () => navigate(item.to);
    });
    return map;
  }, [flatNavItems, navigate]);

  useKeyboard(shortcuts);

  const renderNav = (onItemClick) => {
    // Suporta flat ou grouped: [{label,to,icon}] ou [{title,items:[...]}]
    const groups = nav.length && nav[0].items ? nav : [{ title: null, items: nav }];
    return (
      <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
        {groups.map((group, gi) => (
          <div key={gi}>
            {group.title && (
              <h4 className="text-xs font-semibold text-app-text-secondary uppercase tracking-wide mb-2 px-3">
                {group.title}
              </h4>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onItemClick}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-app-text hover:bg-app-surface-hover'
                      }`
                    }
                  >
                    {item.icon && <Icon as={item.icon} size="sm" />}
                    <span>{item.label}</span>
                    {item.badge != null && (
                      <span className="ml-auto bg-primary text-white text-xs font-semibold rounded-full px-2 py-0.5">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    );
  };

  const logo = (
    <Link to="/" className="flex items-center gap-2 p-4 font-extrabold text-lg text-primary">
      <span>Reviter</span>
    </Link>
  );

  return (
    <div className="min-h-screen flex bg-app-surface">
      {/* Sidebar desktop — fixed em vez de sticky pra evitar quirks de
          position:sticky em flex containers. Coluna da direita compensa
          com `md:ml-64`. */}
      <aside className="hidden md:flex md:w-64 md:flex-col bg-white border-r border-app-border fixed inset-y-0 left-0 z-20">
        {logo}
        {renderNav()}
      </aside>

      {/* Drawer mobile */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        side="left"
        size="md"
      >
        {logo}
        {renderNav(() => setMobileOpen(false))}
      </Drawer>

      {/* Main column — md:ml-64 compensa a sidebar fixa à esquerda. */}
      <div className="flex-1 flex flex-col min-w-0 md:ml-64">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-app-border flex items-center px-4 md:px-6 gap-3 sticky top-0 z-30">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 rounded-md text-app-text hover:bg-app-surface-hover"
            aria-label="Abrir menu"
          >
            <Menu size={20} />
          </button>

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 text-sm text-app-text-secondary bg-app-surface hover:bg-app-surface-hover rounded-lg border border-app-border transition-colors"
          >
            <Search size={14} />
            <span>Buscar…</span>
            <kbd className="ml-2 text-xs font-mono border border-app-border rounded px-1.5 py-0.5 bg-white">
              ⌘K
            </kbd>
          </button>

          <div className="flex-1" />

          {topbarRight}

          <NotificationBell />

          <button
            type="button"
            onClick={() => setShortcutsOpen(true)}
            className="p-2 rounded-md text-app-text-secondary hover:bg-app-surface-hover transition-colors"
            aria-label="Atalhos de teclado"
            title="Atalhos (?)"
          >
            <Keyboard size={18} />
          </button>

          {user && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 p-1 rounded-lg hover:bg-app-surface-hover transition-colors"
                aria-label="Menu do usuário"
                aria-expanded={menuOpen}
              >
                <Avatar name={user.name} src={user.avatarUrl} size="sm" />
                <span className="hidden sm:inline text-sm font-medium text-app-text max-w-[8rem] truncate">
                  {user.name}
                </span>
              </button>
              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setMenuOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-app-border z-20 py-1">
                    <div className="px-3 py-2 border-b border-app-border">
                      <p className="text-sm font-semibold text-app-text truncate">{user.name}</p>
                      {user.email && (
                        <p className="text-xs text-app-text-secondary truncate">{user.email}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-app-surface-hover"
                    >
                      <LogOut size={16} />
                      Sair
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6 overflow-x-hidden">{children}</main>

        <footer className="px-4 md:px-6 py-3 border-t border-app-border bg-white flex justify-end">
          <PoweredBy variant="dark" />
        </footer>
      </div>

      <CommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={flatNavItems}
      />

      <ShortcutsModal
        isOpen={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        groups={[
          {
            title: 'Global',
            items: [
              { label: 'Abrir busca global', keys: ['⌘', 'K'] },
              { label: 'Focar campo de busca na tela', keys: ['/'] },
              { label: 'Abrir este painel', keys: ['?'] },
              { label: 'Fechar overlays', keys: ['Esc'] },
            ],
          },
          ...(flatNavItems.some((it) => it.shortcut)
            ? [
                {
                  title: 'Navegação',
                  items: flatNavItems
                    .filter((it) => it.shortcut)
                    .map((it) => ({
                      label: `Ir para ${it.label}`,
                      keys: ['g', it.shortcut],
                    })),
                },
              ]
            : []),
          {
            title: 'Em formulários',
            items: [{ label: 'Salvar', keys: ['⌘', 'S'] }],
          },
        ]}
      />
    </div>
  );
};

export default DashboardLayout;
